import React, { useState } from 'react';
import { auth, db } from '../../../shared/storage/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import '../styles/AddStudentModal.css';

interface AddStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStudentAdded: () => void;
  parentId: string;
}

const AddStudentModal: React.FC<AddStudentModalProps> = ({ isOpen, onClose, onStudentAdded, parentId }) => {
  const [childFirst, setChildFirst] = useState("");
  const [childLast, setChildLast] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [photoConsent, setPhotoConsent] = useState<"yes" | "no" | null>(null);
  const [grade, setGrade] = useState<string>("");
  const [pickupPreferenceOptions, setPickupPreferenceOptions] = useState<string[]>([]);
  const [pickupOtherText, setPickupOtherText] = useState<string>("");
  const [creatingChild, setCreatingChild] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  async function handleCreateChild() {
    if (!parentId) return;
    if (!childFirst.trim() || !childLast.trim()) return;
    
    setCreatingChild(true);
    setError(null);
    
    try {
      const token = await auth.currentUser?.getIdToken();
      // In the platform, we can probably get the project ID from firebase config or env
      const projectId = (auth.app.options as any).projectId;
      const base = `https://us-central1-${projectId}.cloudfunctions.net`;
      
      const resp = await fetch(`${base}/createStudentAndLinkHTTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          displayName: `${childFirst.trim()} ${childLast.trim()}`.trim(),
          grade: grade || null,
          pickupPreference:
            pickupPreferenceOptions.length > 0
              ? {
                  options: pickupPreferenceOptions,
                  ...(pickupPreferenceOptions.includes("other") && pickupOtherText.trim()
                    ? { otherText: pickupOtherText.trim() }
                    : {}),
                }
              : null,
        }),
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to add child.');
      }

      const json = await resp.json();
      const newId = json?.studentId as string;

      // Save consents on parent document
      const photo = photoConsent === 'yes' ? 'accept' : photoConsent === 'no' ? 'deny' : '';
      const payload: any = {
        childConsents: {
          [newId]: {
            tos: !!tosAccepted,
            photoRelease: photo,
            acceptedAt: new Date().toISOString(), // Using ISO string to match platform pattern if needed, but Firestore serverTimestamp is better
          },
        },
      };
      
      // Use serverTimestamp for Firestore
      payload.childConsents[newId].acceptedAt = serverTimestamp();

      await setDoc(doc(db, 'users', parentId), payload, { merge: true });

      // Reset form
      setChildFirst("");
      setChildLast("");
      setTosAccepted(false);
      setPhotoConsent(null);
      setGrade("");
      setPickupPreferenceOptions([]);
      setPickupOtherText("");
      
      onStudentAdded();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to add child.');
    } finally {
      setCreatingChild(false);
    }
  }

  const pickupOptions: Array<{ key: string; label: string }> = [
    { key: "aftercare", label: "Aftercare" },
    { key: "guardian_pickup", label: "Guardian Pick-up" },
    { key: "bike_walk", label: "Bike/Walk" },
    { key: "other", label: "Other" },
  ];

  function togglePickupPref(key: string) {
    setPickupPreferenceOptions((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      if (!next.includes("other")) {
        setPickupOtherText("");
      }
      return next;
    });
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal add-student-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add a Student</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p className="modal-subtitle">Enter your student's details to link them to your account.</p>

          {error && <div className="error-message">{error}</div>}

          <div className="form-grid">
            <div className="form-group">
              <label>First Name</label>
              <input 
                type="text" 
                value={childFirst} 
                onChange={(e) => setChildFirst(e.target.value)}
                placeholder="Student's first name"
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input 
                type="text" 
                value={childLast} 
                onChange={(e) => setChildLast(e.target.value)}
                placeholder="Student's last name"
              />
            </div>
            <div className="form-group full-width">
              <label>Grade</label>
              <select value={grade} onChange={(e) => setGrade(e.target.value)}>
                <option value="">Select grade</option>
                <option>Pre-Kindergarten</option>
                <option>Kindergarten</option>
                <option>1st Grade</option>
                <option>2nd Grade</option>
                <option>3rd Grade</option>
                <option>4th Grade</option>
                <option>5th Grade</option>
                <option>6th Grade</option>
                <option>7th Grade</option>
                <option>8th Grade</option>
              </select>
            </div>
          </div>

          <div className="pickup-section">
            <h4>Pickup Preference</h4>
            <div className="pickup-options">
              {pickupOptions.map((opt) => (
                <label key={opt.key} className="checkbox-label pickup-checkbox">
                  <input
                    type="checkbox"
                    checked={pickupPreferenceOptions.includes(opt.key)}
                    onChange={() => togglePickupPref(opt.key)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            {pickupPreferenceOptions.includes("other") && (
              <div className="pickup-other">
                <label className="pickup-other-label">Other (please specify)</label>
                <input
                  type="text"
                  value={pickupOtherText}
                  onChange={(e) => setPickupOtherText(e.target.value)}
                  placeholder="e.g., Carpool, extended day, etc."
                />
              </div>
            )}
          </div>

          <div className="consent-section">
            <h4>Permissions & Consent</h4>
            
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={tosAccepted} 
                onChange={(e) => setTosAccepted(e.target.checked)} 
              />
              <span>
                I accept the <a href="https://overture.games/legal/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              </span>
            </label>

            <div className="photo-consent-box">
              <div className="consent-title">Photo & Video Release</div>
              <div className="consent-subtitle">
                <a href="https://overture.games/legal/photo-release" target="_blank" rel="noopener noreferrer">View consent form</a>
              </div>
              <div className="radio-group">
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="photo" 
                    checked={photoConsent === 'yes'} 
                    onChange={() => setPhotoConsent('yes')} 
                  />
                  <span>Yes, I give permission</span>
                </label>
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="photo" 
                    checked={photoConsent === 'no'} 
                    onChange={() => setPhotoConsent('no')} 
                  />
                  <span>No, I do not give permission</span>
                </label>
              </div>
            </div>
          </div>

          <button 
            className="submit-button" 
            disabled={!childFirst || !childLast || !grade || !tosAccepted || photoConsent === null || creatingChild}
            onClick={handleCreateChild}
          >
            {creatingChild ? 'Adding...' : 'Add Student'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStudentModal;



